<template>
    <div class="page">
        <v-container class="order-form" fluid density="compact">
            <v-form ref="orderFormRef" :disabled="!cartItems.length" @submit.prevent="submitOrderAsync">
                <!-- Product amount / Cart items -->
                <section>
                    <div class="section-title">
                        <h4>{{ terms.products_in_cart || terms.cart_title || 'Products in cart' }}</h4>
                    </div>

                    <!-- Empty Cart State / Cart Products List with Transition -->
                    <transition name="fade" mode="out-in">
                        <div v-if="!cartItems.length" key="empty" class="empty-cart-container text-center py-10 px-4">
                            <div class="empty-cart-icon-wrapper mb-3">
                                <v-icon size="72" color="#ffc451"
                                    class="empty-cart-bounce-icon">mdi-cart-outline</v-icon>
                            </div>
                            <h3 class="mt-2 font-weight-bold text-h5 text-dark">{{ terms.cart_empty }}</h3>
                            <p class="text-grey-darken-1 mb-6 text-subtitle-1">{{ terms.cart_empty_description }}</p>
                            <div class="mt-4 max-w-xl mx-auto">
                                <div class="signature-offers-card">
                                    <!-- Interactive Product Tiles Grid with Mobile Swipe -->
                                    <div class="swipeable-container" :class="offersSwipe.bounceClass.value"
                                        @touchstart="offersSwipe.onTouchStart" @touchend="offersSwipe.onTouchEnd"
                                        @click.capture="offersSwipe.onClickCapture">
                                        <transition :name="offersSwipe.transitionName.value" mode="out-in">
                                            <div :key="offersCurrentPage" class="product-tiles-grid">
                                                <div v-for="item in paginatedOffersProducts" :key="item.id"
                                                    :class="['interactive-product-tile', { 'offers-placeholder-tile': item.isPlaceholder }]"
                                                    @click="!item.isPlaceholder && navigateToProductOffer(item.id)"
                                                    :title="!item.isPlaceholder ? getBannerProductTitle(item) : ''">
                                                    <template v-if="!item.isPlaceholder">
                                                        <div class="tile-image-bg"
                                                            :style="{ backgroundImage: `url('${item.image}')` }"></div>
                                                        <div class="tile-badge">
                                                            <span class="tile-title">{{ getBannerProductTitle(item) }}</span>
                                                        </div>
                                                    </template>
                                                </div>
                                            </div>
                                        </transition>
                                    </div>
                                    <v-row v-if="totalOffersPages > 1" justify="center" class="mt-3">
                                        <v-pagination v-model="offersCurrentPage" :length="totalOffersPages"
                                            :total-visible="5" color="avem" active-color="avem" rounded="circle"
                                            class="gallery-pagination"></v-pagination>
                                    </v-row>
                                </div>
                            </div>
                        </div>

                        <!-- Cart Products List -->
                        <div v-else key="cart-list">
                            <v-row justify="center" align="stretch" class="mx-n1 mx-sm-0">
                                <v-col cols="6" sm="6" md="6" lg="4" xl="4" v-for="item in cartItems" :key="item.id"
                                    class="px-1 px-sm-3 mb-2 mb-sm-4">
                                    <v-card class="h-100 product-card d-flex flex-column" elevation="3">
                                        <div class="card-content-top flex-grow-1 d-flex flex-column">
                                            <!-- Product Header -->
                                            <v-card-title class="wrap-title responsive-title pa-3 text-center">
                                                <div class="product-name">{{ item.product.header }}</div>
                                                <div class="product-price-row mt-1">
                                                    <b>{{ formatNumber(item.product.priceEuro) }}{{ terms.currency_euro
                                                    }}</b> |
                                                    <b>{{ formatNumber(item.product.priceLeva) }}{{ terms.currency_leva
                                                    }}</b>
                                                </div>
                                            </v-card-title>
                                            <div class="d-flex align-center justify-center flex-grow-1 py-2">
                                                <v-img :src="item.product.imageSrc" class="product-image"
                                                    max-height="180" contain></v-img>
                                            </div>
                                        </div>

                                        <!-- Quantity Stepper & Remove Button -->
                                        <v-card-text class="mt-auto d-flex align-center justify-center ga-3 py-3">
                                            <button type="button" class="btn-remove-cart"
                                                @click.prevent.stop="removeItem(item.id, $event)"
                                                :title="terms.remove_from_cart">
                                                <v-icon size="small">mdi-trash-can-outline</v-icon>
                                            </button>
                                            <div class="quantity-stepper d-inline-flex align-center">
                                                <button type="button" class="step-btn"
                                                    @click.prevent.stop="decreaseQuantity(item, $event)"
                                                    :disabled="item.quantity <= 1">-</button>
                                                <input type="number" class="qty-input" :value="item.quantity"
                                                    @input="e => onQuantityInput(item.id, e.target.value)" min="1"
                                                    max="100" />
                                                <button type="button" class="step-btn"
                                                    @click.prevent.stop="increaseQuantity(item, $event)"
                                                    :disabled="item.quantity >= 100">+</button>
                                            </div>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <!-- Price Summary (Hidden for now, info moved inside banner) -->
                            <v-row v-if="false" class="mt-4">
                                <v-col cols="12">
                                    <div v-if="hasOrderPrice" class="text-center">
                                        <b>{{ terms.price }}: </b>{{ orderPriceEuro }}{{ terms.currency_euro }} | {{
                                            orderPriceLeva }}{{
                                            terms.currency_leva }}
                                    </div>
                                    <div v-if="hasDeliveryCost || hasFreeDelivery" class="text-center mt-1">
                                        <b>{{ terms.delivery_cost || 'Delivery' }}: </b>
                                        <span v-if="hasFreeDelivery" class="text-success font-weight-bold">{{ freeDeliveryText }}</span>
                                        <span v-else-if="hasDeliveryCost">{{ effectiveDeliveryCostEuro }}{{ terms.currency_euro }} | {{ effectiveDeliveryCostLeva }}{{ terms.currency_leva }}</span>
                                    </div>
                                    <div v-if="hasSavedDeliveryAmount"
                                        class="text-center mt-1 text-subtitle-1 font-weight-bold text-success">
                                        💰 {{ savedDeliveryText }}
                                    </div>
                                    <h1 class="text-center mt-2" v-if="hasFinalPrice">
                                        {{ terms.total }}: {{ orderFinalPriceEuro }}{{ terms.currency_euro }} |
                                        {{ orderFinalPriceLeva }}{{ terms.currency_leva }}
                                    </h1>
                                </v-col>
                            </v-row>
                            <!-- Signature Offers Banner Section -->
                            <div v-if="availableBannerProducts.length > 0" class="mt-6 mt-md-8 pt-2">
                                <h2 class="offers-outer-title text-center mb-3 mb-md-4">
                                    {{ offersExploreOtherTitle }}
                                </h2>
                                <div class="signature-offers-card">
                                    <!-- Interactive Product Tiles Grid with Mobile Swipe -->
                                    <div class="swipeable-container" :class="offersSwipe.bounceClass.value"
                                        @touchstart="offersSwipe.onTouchStart" @touchend="offersSwipe.onTouchEnd"
                                        @click.capture="offersSwipe.onClickCapture">
                                        <transition :name="offersSwipe.transitionName.value" mode="out-in">
                                            <div :key="offersCurrentPage" class="product-tiles-grid">
                                                <div v-for="item in paginatedOffersProducts" :key="item.id"
                                                    :class="['interactive-product-tile', { 'offers-placeholder-tile': item.isPlaceholder }]"
                                                    @click="!item.isPlaceholder && navigateToProductOffer(item.id)"
                                                    :title="!item.isPlaceholder ? getBannerProductTitle(item) : ''">
                                                    <template v-if="!item.isPlaceholder">
                                                        <div class="tile-image-bg"
                                                            :style="{ backgroundImage: `url('${item.image}')` }"></div>
                                                        <div class="tile-badge">
                                                            <span class="tile-title">{{ getBannerProductTitle(item) }}</span>
                                                        </div>
                                                    </template>
                                                </div>
                                            </div>
                                        </transition>
                                    </div>
                                    <v-row v-if="totalOffersPages > 1" justify="center" class="mt-3">
                                        <v-pagination v-model="offersCurrentPage" :length="totalOffersPages"
                                            :total-visible="5" color="avem" active-color="avem" rounded="circle"
                                            class="gallery-pagination"></v-pagination>
                                    </v-row>
                                </div>
                            </div>
                        </div>
                    </transition>
                </section>

                <!-- Personal Info -->
                <section>
                    <div class="section-title">
                        <h4>{{ terms.order_section_personal_info }}</h4>
                    </div>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <!-- First Name -->
                            <v-text-field name="firstName" v-model="order.firstName" :label="terms.order_first_name"
                                :rules="[rules.required]" outlined density="comfortable" />
                            <!-- Last Name -->
                            <v-text-field name="lastName" v-model="order.lastName" :label="terms.order_last_name"
                                :rules="[rules.required]" outlined density="comfortable" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <!-- Phone -->
                            <v-text-field name="phone" v-model="order.phone" :label="terms.order_phone"
                                :rules="[rules.required, rules.phone('BG')]" outlined density="comfortable" />
                            <!-- Email -->
                            <v-text-field name="email" v-model="order.email" :label="terms.order_email"
                                :rules="[rules.required, rules.email]" outlined density="comfortable" />
                        </v-col>
                    </v-row>
                </section>

                <!-- Delivery Option -->
                <section>
                    <div class="section-title">
                        <h4>{{ terms.order_section_delivery_option }}</h4>
                    </div>
                    <!-- Delivery Companies -->
                    <v-row>
                        <v-col cols="12">
                            <v-radio-group v-model="order.deliveryCompany" inline class="delivery-company-radio-group">
                                <v-radio v-for="(comp, index) in deliveryCompanies" :key="index" :label="comp.text"
                                    :value="comp.value">
                                    <template v-slot:label>
                                        <span class="special-image-label">
                                            <v-img :src="comp.logoPath" height="40" width="100" contain></v-img>
                                        </span>
                                    </template>
                                </v-radio>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                    <!-- Delivery Types -->
                    <v-row>
                        <v-col cols="12">
                            <v-radio-group v-model="order.deliveryTo" inline class="delivery-type-radio-group">
                                <v-radio v-for="(type, index) in deliveryTypes" :key="index" :value="type.value">
                                    <template v-slot:label>
                                        <span
                                            class="delivery-type-card d-inline-flex align-center ga-2 py-2 px-3 rounded-lg"
                                            :class="{ 'delivery-type-card--selected': order.deliveryTo === type.value }">
                                            <v-icon :color="order.deliveryTo === type.value ? 'avem' : 'grey-darken-1'"
                                                size="24">{{ type.icon }}</v-icon>
                                            <span class="delivery-type-text font-weight-medium">{{ type.text }}</span>
                                        </span>
                                    </template>
                                </v-radio>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                    <!-- Delivery to Address -->
                    <v-row v-if="order.deliveryTo === DELIVERY_TO_ADDRESS">
                        <v-col cols="12" sm="3">
                            <v-autocomplete name="city" v-model="order.city" :label="terms.order_site"
                                :rules="[rules.required]" @input="searchCitiesInput" :items="foundCities"
                                item-value="id" item-title="name" :loading="formLoading" autocomplete="nope" no-filter
                                outlined clearable density="comfortable" />
                        </v-col>
                        <v-col cols="7" sm="7">
                            <v-autocomplete name="street" v-model="order.street" :label="terms.order_street_name"
                                :rules="[rules.required]" @input="searchStreetInput" :items="foundStreets"
                                item-value="id" item-title="name" :disabled="!order.city" :loading="formLoading"
                                autocomplete="nope" no-filter outlined clearable density="comfortable" />
                        </v-col>
                        <v-col cols="5" sm="2">
                            <v-text-field name="streetNumber" v-model="order.streetNumber"
                                :label="terms.order_street_number"
                                :rules="[rules.required, rules.isNumber, rules.isPositive]" :disabled="!order.street"
                                outlined density="comfortable" />
                        </v-col>
                    </v-row>
                    <v-row v-if="order.deliveryTo === DELIVERY_TO_ADDRESS">
                        <v-col cols="6" sm="3">
                            <v-text-field name="building" v-model="order.building" :label="terms.order_building"
                                :disabled="!order.street" outlined density="comfortable" />
                        </v-col>
                        <v-col cols="6" sm="3">
                            <v-text-field name="entrance" v-model="order.entrence" :label="terms.order_entrence"
                                :disabled="!order.street" outlined density="comfortable" />
                        </v-col>
                        <v-col cols="6" sm="3">
                            <v-text-field name="floor" v-model="order.floor" :label="terms.order_floor"
                                :disabled="!order.street" outlined density="comfortable" />
                        </v-col>
                        <v-col cols="6" sm="3">
                            <v-text-field name="apartmentNumber" v-model="order.apartmentNumber"
                                :label="terms.order_apartment_number" :disabled="!order.street" outlined
                                density="comfortable" />
                        </v-col>
                    </v-row>
                    <!-- Delivery to Office -->
                    <v-row v-if="order.deliveryTo === DELIVERY_TO_OFFICE">
                        <v-col cols="12" sm="3">
                            <v-autocomplete name="city" v-model="order.city" :label="terms.order_site"
                                :rules="[rules.required]" @input="searchCitiesInput" :items="foundCities"
                                item-value="id" item-title="name" :loading="formLoading" autocomplete="nope" no-filter
                                outlined clearable density="comfortable" />
                        </v-col>
                        <v-col cols="12" sm="9">
                            <v-autocomplete name="office" v-model="order.office" :label="terms.order_office"
                                :rules="[rules.required]" @input="searchOfficesInput" :items="foundOffices"
                                item-value="id" item-title="title" :disabled="!order.city" :loading="formLoading"
                                autocomplete="nope" no-filter outlined clearable density="comfortable">
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                </section>

                <!-- Payment Option -->
                <section>
                    <div class="section-title">
                        <h4>{{ terms.order_section_payment_option }}</h4>
                    </div>
                    <v-row>
                        <v-col cols="12">
                            <v-radio-group v-model="order.paymentWith" inline class="payment-radio-group">
                                <v-radio v-for="(type, index) in paymentTypes" :key="index" :value="type.value"
                                    :disabled="type.disabled">
                                    <template v-slot:label>
                                        <span
                                            class="payment-type-card d-inline-flex align-center ga-2 py-2 px-3 rounded-lg"
                                            :class="{ 'payment-type-card--selected': order.paymentWith === type.value }">
                                            <v-icon :color="order.paymentWith === type.value ? 'avem' : 'grey-darken-1'"
                                                size="24">{{ type.icon }}</v-icon>
                                            <span class="payment-type-text font-weight-medium">{{ type.text }}</span>
                                            <span v-if="type.value === PAYMENT_STRIPE"
                                                class="d-inline-flex align-center ga-1 ml-1">
                                                <v-icon size="small"
                                                    color="blue-darken-2">mdi-credit-card-outline</v-icon>
                                                <v-icon size="small"
                                                    color="amber-darken-2">mdi-credit-card-chip</v-icon>
                                            </span>
                                        </span>
                                    </template>
                                </v-radio>
                            </v-radio-group>
                        </v-col>
                    </v-row>
                    <v-row v-if="order.paymentWith === PAYMENT_BANK_TRANSFER">
                        <v-col cols="12">
                            <div class="text-left pay-attention-note">* {{ terms.order_payment_bank_transfer_p1 }}</div>
                        </v-col>
                    </v-row>
                    <v-row v-if="order.paymentWith === PAYMENT_STRIPE">
                        <v-col cols="12">
                            <div class="text-left pay-attention-note">* {{ terms.order_payment_stripe_p1 }}</div>
                        </v-col>
                    </v-row>
                </section>

                <!-- Additional Options -->
                <section>
                    <div class="section-title">
                        <h4>{{ terms.order_section_additional_option }}</h4>
                    </div>
                    <!-- Maybe can be active at later point
                    <v-row>
                        <v-col cols="12">
                            <v-checkbox v-model="order.includeFiscalReceipt" :label="terms.order_include_fiscal_receipt" />
                        </v-col>
                    </v-row>-->
                    <v-row>
                        <v-col cols="12">
                            <v-checkbox v-model="order.includeInvoice" :label="terms.order_include_invoice" />
                        </v-col>
                    </v-row>
                    <v-row v-if="order.includeInvoice">
                        <v-col cols="12" sm="4">
                            <v-text-field v-model="order.invoiceCompanyName" :label="terms.order_invoice_company_name"
                                :rules="order.includeInvoice ? [rules.required] : []" outlined />
                        </v-col>
                        <v-col cols="6" sm="4">
                            <v-text-field v-model="order.invoiceBulstat" :label="terms.order_invoice_bulstat"
                                :rules="order.includeInvoice ? [rules.required, rules.bulstat] : []" outlined />
                        </v-col>
                        <v-col cols="6" sm="4">
                            <v-text-field v-model="order.invoiceMol" :label="terms.order_invoice_mol"
                                :rules="order.includeInvoice ? [rules.required] : []" outlined />
                        </v-col>
                    </v-row>
                    <!--Invoice Special Address-->
                    <v-row v-if="order.includeInvoice">
                        <v-col cols="12" sm="3">
                            <v-text-field v-model="order.invoiceCity" :label="terms.order_site"
                                :rules="order.includeInvoice ? [rules.required] : []"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="9">
                            <v-text-field v-model="order.invoiceAddress" :label="terms.order_address"
                                :rules="order.includeInvoice ? [rules.required] : []"></v-text-field>
                        </v-col>
                    </v-row>
                    <!--Invoice DDS-->
                    <v-row v-if="order.includeInvoice">
                        <v-col cols="12">
                            <v-checkbox v-model="order.invoiceRegisterByDDS"
                                :label="terms.order_invoice_dds"></v-checkbox>
                        </v-col>
                    </v-row>
                    <!--Additional Instructions-->
                    <v-row>
                        <v-col cols="12">
                            <v-textarea v-model="order.additionalInstructions"
                                :label="terms.order_additional_instructions"
                                :placeholder="terms.order_additional_instructions_placeholder" rows="3"
                                :rules="[rules.maxCharachters(500)]" outlined />
                        </v-col>
                    </v-row>
                    <!--Acknowledgement-->
                    <v-row class="acknowledgement">
                        <v-col cols="12">
                            <!--GDPR Policy-->
                            <v-checkbox v-model="order.acknowledgedParsonalDataPolicy" :rules="[rules.requiredBool]">
                                <template v-slot:label>
                                    <a :href="`/documents/${terms.lang}/gdpr.pdf`" class="note-color" target="_blank">{{
                                        terms.order_acknowledge_personal_data_policy }}</a>
                                </template>
                            </v-checkbox>
                            <!--Copmpany Policy-->
                            <v-checkbox v-model="order.acknowledgedCompanyPolicy" :rules="[rules.requiredBool]">
                                <template v-slot:label>
                                    <a :href="`/documents/${terms.lang}/policy.pdf`" class="note-color"
                                        target="_blank">{{ terms.order_acknowledge_company_policy }}</a>
                                </template>
                            </v-checkbox>
                        </v-col>
                    </v-row>
                </section>

                <!-- Submit -->
                <v-row>
                    <v-col cols="12" class="text-center">
                        <v-btn type="submit" color="avem" large density="comfortable" :disabled="!cartItems.length"
                            :loading="showLoadingOrder">
                            {{ terms.order_submit }}
                        </v-btn>
                    </v-col>
                    <v-col cols="12" v-if="orderValidationMessages.length">
                        <div v-for="(message, index) in orderValidationMessages" :key="index"
                            class="error-message text-center">
                            {{ message }}
                        </div>
                    </v-col>
                </v-row>
            </v-form>
        </v-container>
    </div>
</template>

<script setup>
import { reactive, ref, computed, inject, watch, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from 'vue-router';
import _ from 'lodash';
import LZString from 'lz-string';
import { fbPixelOrder } from '@/analytics/facebook-pixel';
import { gtagOrder } from '@/analytics/gtag';
import { useSwipePagination } from '@/utils/useSwipePagination';


const mobileDebug = {
    log: (message, data) => {
        console.log(`[MOBILE DEBUG] ${message}`, data);

        // Also send to a visible element on mobile
        const debugDiv = document.getElementById('mobile-debug') || (() => {
            const div = document.createElement('div');
            div.id = 'mobile-debug';
            div.style.cssText = `
                    position: fixed; 
                    top: 0; 
                    left: 0; 
                    background: rgba(0,0,0,0.8); 
                    color: white; 
                    padding: 10px; 
                    font-size: 12px; 
                    max-height: 200px; 
                    overflow-y: auto; 
                    z-index: 9999;
                    width: 100%;
                `;
            document.body.appendChild(div);
            return div;
        })();

        debugDiv.innerHTML += `<div>${new Date().toLocaleTimeString()}: ${message} ${JSON.stringify(data)}</div>`;
    }
};

const CURRENCY = { LEVA: 1, EURO: 2 };
const PAYMENT_COD = 1;
const PAYMENT_BANK_TRANSFER = 2;
const PAYMENT_STRIPE = 3;
const DELIVERY_COMPANY_SPEEDY = 1;
const DELIVERY_COMPANY_ECONT = 2;
const DELIVERY_TO_ADDRESS = 1;
const DELIVERY_TO_OFFICE = 2;
const mathRound = function (num, decimals = 2, mode = "nearest") {
    if (!Number.isFinite(num))
        return num; // keep NaN/�Infinity as is
    const sign = Math.sign(num);
    const scale = 10 ** decimals;

    let scaled = num * scale;
    let intPart;

    if (mode === "away") {
        intPart = scaled >= 0 ? Math.ceil(scaled) : Math.floor(scaled);
    } else { // "nearest"
        intPart = Math.round(Math.abs(scaled)) * (sign || 1);
    }

    let result = intPart / scale;
    // remove negative zero (-0)
    if (Object.is(result, -0)) result = 0;
    return result;
}

const axios = inject('axios');
const store = useStore();
const router = useRouter();
const terms = computed(() => store.state.terms);
const rules = computed(() => store.state.validationRules);

const bannerProducts = [
    { id: 1, code: 'TM1', image: '/img/tape_dispenser_new.jpg', title: 'Tape Dispenser AVEM' },
    { id: 2, code: 'L1', image: '/img/L1.jpg', title: 'Band AVEM' },
    { id: 3, code: 'TS60', image: '/img/tape.png', title: 'Solvent Tape 48/60m' },
    { id: 6, code: 'TA60', image: '/img/60_acrill.jpg', title: 'Acrylic Tape 48/60m' },
    { id: 8, code: 'TA100', image: '/img/100_acrill.jpg', title: 'Acrylic Tape 48/100m' },
    { id: 7, code: 'TS100', image: '/img/100_solvent.jpg', title: 'Solvent Tape 48/100m' },
    { id: 9, code: 'TQ120', image: '/img/Power-120_quiet.jpg', title: 'Quiet Tape 48/120m' },
    { id: 13, code: 'TA60/6', image: '/img/60_acrill6.jpg', title: 'Acrylic Tape 6 pcs' },
    { id: 14, code: 'TA60MA1', image: '/img/TA60MA1.jpg', title: 'Dispenser + 1pc. Acrylic' },
    { id: 10, code: 'TA60PR6', image: '/img/TA60PR6.jpg', title: 'Dispenser + 6pcs. Acrylic' },
    { id: 4, code: 'TS60PR', image: '/img/promo_dispenser_and_tape.jpg', title: 'Dispenser + 6pcs. Solvent' },
    { id: 11, code: 'TQ120PR', image: '/img/TQ120PR.jpg', title: 'Dispenser + 3pcs. Quiet' },
    { id: 5, code: 'TA36PR', image: '/img/TA60PR36.jpg', title: 'Dispenser + 36pcs. Acrylic' },
];

const getBannerProductTitle = (item) => {
    if (!item) return '';
    const found = store.state.products?.find(p => p.id === item.id);
    if (found?.header) return found.header;
    return item.title || item.titleEn || '';
};

const navigateToProductOffer = (productId) => {
    router.push({ path: '/offers', query: { product: productId } });
};

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200);
const updateWindowWidth = () => {
    windowWidth.value = window.innerWidth;
};

const offersCurrentPage = ref(1);

const offersItemsPerPage = computed(() => {
    if (windowWidth.value < 600) return 4; // 2 cols x 2 rows = 4 items max per page
    if (windowWidth.value < 960) return 6; // 3 cols x 2 rows = 6 items max per page
    return 8; // 4 cols x 2 rows = 8 items max per page
});

const cartItems = computed(() => store.getters.cartItems);
const cartItemCount = computed(() => store.getters.cartItemCount);
const cartTotalEuro = computed(() => store.getters.cartTotalEuro);
const cartTotalLeva = computed(() => store.getters.cartTotalLeva);
const products = computed(() => cartItems.value.map(item => ({ ...item.product, quantity: item.quantity })));

const cartProductIds = computed(() => {
    return cartItems.value.map(item => item.product?.id || item.id);
});

const availableBannerProducts = computed(() => {
    return bannerProducts.filter(p => !cartProductIds.value.includes(p.id));
});

const totalOffersPages = computed(() => {
    return Math.ceil(availableBannerProducts.value.length / offersItemsPerPage.value);
});

const offersSwipe = useSwipePagination(offersCurrentPage, totalOffersPages);

const paginatedOffersProducts = computed(() => {
    const start = (offersCurrentPage.value - 1) * offersItemsPerPage.value;
    const realItems = availableBannerProducts.value.slice(start, start + offersItemsPerPage.value);
    if (totalOffersPages.value <= 1) {
        return realItems;
    }
    const items = [...realItems];
    const totalNeeded = offersItemsPerPage.value;
    while (items.length < totalNeeded) {
        items.push({ isPlaceholder: true, id: `placeholder-order-offer-${items.length}` });
    }
    return items;
});

const order = reactive({
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    deliveryCompany: DELIVERY_COMPANY_SPEEDY,
    deliveryTo: DELIVERY_TO_ADDRESS,
    paymentWith: PAYMENT_COD,
    city: null,
    region: null,
    street: null,
    streetNumber: null,
    building: null,
    entrance: null,
    floor: null,
    apartmentNumber: null,
    office: null,
    includeInvoice: false,
    includeFiscalReceipt: true,
    invoiceCompanyName: null,
    invoiceBulstat: null,
    invoiceMol: null,
    invoiceCity: null,
    invoiceAddress: null,
    additionalInstructions: null,
    acknowledgedParsonalDataPolicy: null,
    acknowledgedCompanyPolicy: null
});

const debounceTime = 400;
const SESSION_STORAGE_KEY = 'avem_order_form_data';

const foundCities = ref([]);
const foundStreets = ref([]);
const foundOffices = ref([]);

const loadSavedOrderState = () => {
    try {
        const savedRaw = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (savedRaw) {
            const parsed = JSON.parse(savedRaw);
            if (parsed.order && typeof parsed.order === 'object') {
                const orderData = { ...parsed.order };
                delete orderData.acknowledgedParsonalDataPolicy;
                delete orderData.acknowledgedCompanyPolicy;
                if (orderData.includeInvoice && !orderData.invoiceCompanyName?.trim()) {
                    orderData.includeInvoice = false;
                }
                Object.assign(order, orderData);
            }
            if (parsed.foundCities && Array.isArray(parsed.foundCities) && parsed.foundCities.length > 0) {
                foundCities.value = parsed.foundCities;
            }
            if (parsed.foundStreets && Array.isArray(parsed.foundStreets) && parsed.foundStreets.length > 0) {
                foundStreets.value = parsed.foundStreets;
            }
            if (parsed.foundOffices && Array.isArray(parsed.foundOffices) && parsed.foundOffices.length > 0) {
                foundOffices.value = parsed.foundOffices;
            }
        }
    } catch (e) {
        console.warn('Failed to load saved order state from sessionStorage:', e);
    }
};

const saveOrderState = () => {
    try {
        const orderCopy = { ...order };
        delete orderCopy.acknowledgedParsonalDataPolicy;
        delete orderCopy.acknowledgedCompanyPolicy;

        const dataToSave = {
            order: orderCopy,
            foundCities: foundCities.value,
            foundStreets: foundStreets.value,
            foundOffices: foundOffices.value
        };
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (e) {
        console.warn('Failed to save order state to sessionStorage:', e);
    }
};

// Restore saved order inputs immediately
loadSavedOrderState();

// Persist any form changes automatically
watch(order, () => {
    saveOrderState();
}, { deep: true });

watch([foundCities, foundStreets, foundOffices], () => {
    saveOrderState();
}, { deep: true });
const showLoadingOrder = ref(false);
const deliveryCompanies = ref([
    { text: terms.value.delivery_company_speedy, value: DELIVERY_COMPANY_SPEEDY, logoPath: "/img/speedy_logo.png" },
    { text: terms.value.delivery_company_econt, value: DELIVERY_COMPANY_ECONT, logoPath: "/img/econt_logo.png" },
]);
const deliveryTypes = ref([
    { text: terms.value.order_delivery_to_address, value: DELIVERY_TO_ADDRESS, icon: "mdi-home-map-marker" },
    { text: terms.value.order_delivery_to_office, value: DELIVERY_TO_OFFICE, icon: "mdi-store-outline" },
]);
const isCardPaymentEnabled = computed(() => {
    if (terms.value?.enableCardPayments !== undefined && terms.value?.enableCardPayments !== null) {
        return Boolean(terms.value.enableCardPayments);
    }
    const val = import.meta.env.VITE_APP_ENABLE_CARD_PAYMENTS ?? import.meta.env.VITE_APP_ENABLE_STRIPE_PAYMENTS;
    if (val === undefined || val === null || val === '') return false;
    return String(val).toLowerCase() === 'true' || String(val) === '1';
});

const paymentTypes = computed(() => {
    const types = [
        { text: terms.value.order_payment_cash_on_delivery, value: PAYMENT_COD, disabled: false, icon: "mdi-cash-multiple" },
        { text: terms.value.order_payment_bank_transfer, value: PAYMENT_BANK_TRANSFER, disabled: false, icon: "mdi-bank-transfer" },
    ];
    if (isCardPaymentEnabled.value) {
        types.push({ text: terms.value.order_payment_stripe, value: PAYMENT_STRIPE, disabled: false, icon: "mdi-credit-card-outline" });
    }
    return types;
});

watch(isCardPaymentEnabled, (enabled) => {
    if (!enabled && order.paymentWith === PAYMENT_STRIPE) {
        order.paymentWith = PAYMENT_COD;
    }
}, { immediate: true });
const orderFormRef = ref(null);
const formLoading = ref(false);
const deliveryCostLeva = ref(null);
const deliveryCostEuro = ref(null);
const originalDeliveryCostLeva = ref(null);
const originalDeliveryCostEuro = ref(null);
const deliveryCostIsValid = ref(true);
const orderValidationMessages = ref([]);

const createTapeDispenserParticle = () => {
    const flyer = document.createElement('div');
    flyer.className = 'fly-cart-particle';
    flyer.style.backgroundImage = "url('/img/tape_dispenser_new.jpg')";
    flyer.style.backgroundSize = 'contain';
    flyer.style.backgroundRepeat = 'no-repeat';
    flyer.style.backgroundPosition = 'center';
    flyer.style.backgroundColor = '#141414';
    flyer.style.border = '2px solid #ffc451';
    flyer.style.borderRadius = '50%';
    flyer.style.boxShadow = '0 8px 24px rgba(255, 196, 81, 0.85)';
    flyer.style.position = 'fixed';
    flyer.style.zIndex = '999999';
    flyer.style.pointerEvents = 'none';
    flyer.style.transition = 'all 1.1s cubic-bezier(0.22, 1, 0.36, 1)';
    return flyer;
};

const flyToCartAnimation = (startEvent) => {
    const cartHeader = document.querySelector('#order-cart-icon-btn') || document.querySelector('#order-cart-banner') || document.querySelector('#header-cart-btn') || document.querySelector('.cart-btn');
    if (!cartHeader) return;

    let startX = window.innerWidth / 2;
    let startY = window.innerHeight / 2;
    if (startEvent && startEvent.clientX !== undefined) {
        startX = startEvent.clientX;
        startY = startEvent.clientY;
    } else if (startEvent && startEvent.target) {
        const rect = startEvent.target.getBoundingClientRect();
        startX = rect.left + rect.width / 2;
        startY = rect.top + rect.height / 2;
    }

    const cartRect = cartHeader.getBoundingClientRect();
    const targetX = cartRect.left + cartRect.width / 2;
    const targetY = cartRect.top + cartRect.height / 2;

    const flyer = createTapeDispenserParticle();
    flyer.style.left = `${startX - 28}px`;
    flyer.style.top = `${startY - 28}px`;
    flyer.style.width = '56px';
    flyer.style.height = '56px';

    document.body.appendChild(flyer);

    requestAnimationFrame(() => {
        flyer.style.left = `${targetX - 16}px`;
        flyer.style.top = `${targetY - 16}px`;
        flyer.style.width = '32px';
        flyer.style.height = '32px';
        flyer.style.opacity = '0.2';
        flyer.style.transform = 'scale(0.5) rotate(360deg)';
    });

    setTimeout(() => {
        if (flyer.parentNode) {
            flyer.parentNode.removeChild(flyer);
        }
        cartHeader.classList.add('cart-bump-animate');
        setTimeout(() => {
            cartHeader.classList.remove('cart-bump-animate');
        }, 400);
    }, 1100);
};

const flyFromCartAnimation = (targetEvent) => {
    const cartHeader = document.querySelector('#order-cart-icon-btn') || document.querySelector('#order-cart-banner') || document.querySelector('#header-cart-btn') || document.querySelector('.cart-btn');
    if (!cartHeader) return;

    const cartRect = cartHeader.getBoundingClientRect();
    const startX = cartRect.left + cartRect.width / 2;
    const startY = cartRect.top + cartRect.height / 2;

    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let targetEl = null;

    if (targetEvent && targetEvent.clientX !== undefined) {
        targetX = targetEvent.clientX;
        targetY = targetEvent.clientY;
        targetEl = targetEvent.target ? targetEvent.target.closest('.product-card') : null;
    } else if (targetEvent && targetEvent.target) {
        const rect = targetEvent.target.getBoundingClientRect();
        targetX = rect.left + rect.width / 2;
        targetY = rect.top + rect.height / 2;
        targetEl = targetEvent.target.closest('.product-card');
    }

    const flyer = createTapeDispenserParticle();
    flyer.style.left = `${startX - 16}px`;
    flyer.style.top = `${startY - 16}px`;
    flyer.style.width = '32px';
    flyer.style.height = '32px';

    document.body.appendChild(flyer);

    cartHeader.classList.add('cart-bump-animate');
    setTimeout(() => {
        cartHeader.classList.remove('cart-bump-animate');
    }, 400);

    requestAnimationFrame(() => {
        flyer.style.left = `${targetX - 28}px`;
        flyer.style.top = `${targetY - 28}px`;
        flyer.style.width = '56px';
        flyer.style.height = '56px';
        flyer.style.opacity = '0.2';
        flyer.style.transform = 'scale(0.5) rotate(-360deg)';
    });

    setTimeout(() => {
        if (flyer.parentNode) {
            flyer.parentNode.removeChild(flyer);
        }
        if (targetEl) {
            targetEl.classList.add('card-shake-animate');
            setTimeout(() => {
                targetEl.classList.remove('card-shake-animate');
            }, 400);
        }
    }, 1100);
};

const decreaseQuantity = (item, event) => {
    if (item.quantity > 1) {
        store.commit('updateCartQuantity', { productId: item.id, quantity: item.quantity - 1 });
        flyFromCartAnimation(event, false);
    }
};
const increaseQuantity = (item, event) => {
    if (item.quantity < 100) {
        store.commit('updateCartQuantity', { productId: item.id, quantity: item.quantity + 1 });
        flyToCartAnimation(event, item.product?.imageSrc);
    }
};
const onQuantityInput = (productId, val) => {
    const qty = parseInt(val) || 1;
    store.commit('updateCartQuantity', { productId, quantity: qty });
};
const removeItem = (productId, event) => {
    flyFromCartAnimation(event, true);
    store.commit('removeFromCart', productId);
};
const formatNumber = (val) => {
    if (val === undefined || val === null || isNaN(val)) return '';
    return Number(val).toFixed(2);
};
const navigateToRoute = (route) => {
    router.push(route);
};
const getOrderPrice = (currency) => {
    let amountAllProducts = [];
    if (currency == CURRENCY.LEVA)
        amountAllProducts = _.map(products.value, (p) => p.quantity * p.priceLeva);
    else if (currency == CURRENCY.EURO)
        amountAllProducts = _.map(products.value, (p) => p.quantity * p.priceEuro);
    const sumPriceAllProducts = _.sum(amountAllProducts);
    const result = mathRound(sumPriceAllProducts, 2);
    return result;
};
const calculatedDeliveryCostStore = computed(() => store.getters.calculatedDeliveryCost);

const effectiveDeliveryCostEuro = computed(() => {
    if (deliveryCostEuro.value !== null && deliveryCostEuro.value !== undefined) {
        return deliveryCostEuro.value;
    }
    if (calculatedDeliveryCostStore.value?.isValid && calculatedDeliveryCostStore.value?.euro !== null && calculatedDeliveryCostStore.value?.euro !== undefined) {
        return calculatedDeliveryCostStore.value.euro;
    }
    return null;
});

const effectiveDeliveryCostLeva = computed(() => {
    if (deliveryCostLeva.value !== null && deliveryCostLeva.value !== undefined) {
        return deliveryCostLeva.value;
    }
    if (calculatedDeliveryCostStore.value?.isValid && calculatedDeliveryCostStore.value?.leva !== null && calculatedDeliveryCostStore.value?.leva !== undefined) {
        return calculatedDeliveryCostStore.value.leva;
    }
    return null;
});

const getDelivaryCost = (currency) => {
    if (currency == CURRENCY.LEVA)
        return mathRound(effectiveDeliveryCostLeva.value ?? 0, 2);
    else if (currency == CURRENCY.EURO)
        return mathRound(effectiveDeliveryCostEuro.value ?? 0, 2);
    return null;
};
const getOrderFinalPrice = (currency) => {
    const price = getOrderPrice(currency);
    const delivery = getDelivaryCost(currency);
    const totalPrice = price + delivery;
    const result = mathRound(totalPrice, 2);
    return result;
};

const orderQuantity = computed(() => {
    const quantityAllProducts = _.map(products.value, (p) => p.quantity);
    const quantity = _.sum(quantityAllProducts);
    return quantity;
});
const orderPriceLeva = computed(() => getOrderPrice(CURRENCY.LEVA));
const orderPriceEuro = computed(() => getOrderPrice(CURRENCY.EURO));
const orderFinalPriceLeva = computed(() => getOrderFinalPrice(CURRENCY.LEVA));
const orderFinalPriceEuro = computed(() => getOrderFinalPrice(CURRENCY.EURO));
const hasOrderPrice = computed(() => {
    return orderPriceEuro.value > 0;
});
const hasDeliveryCost = computed(() => {
    return effectiveDeliveryCostEuro.value !== null;
});
const hasFreeDelivery = computed(() => {
    return orderPriceEuro.value >= store.getters.freeDeliveryThreshold || effectiveDeliveryCostEuro.value === 0;
});

const savedDeliveryEuro = computed(() => {
    if (calculatedDeliveryCostStore.value?.originalEuro && calculatedDeliveryCostStore.value.originalEuro > 0) {
        return mathRound(calculatedDeliveryCostStore.value.originalEuro, 2);
    }
    if (deliveryCostIsValid.value && originalDeliveryCostEuro.value > 0) {
        return mathRound(originalDeliveryCostEuro.value, 2);
    }
    return 0;
});
const savedDeliveryLeva = computed(() => {
    if (calculatedDeliveryCostStore.value?.originalLeva && calculatedDeliveryCostStore.value.originalLeva > 0) {
        return mathRound(calculatedDeliveryCostStore.value.originalLeva, 2);
    }
    if (deliveryCostIsValid.value && originalDeliveryCostLeva.value > 0) {
        return mathRound(originalDeliveryCostLeva.value, 2);
    }
    return 0;
});
const hasSavedDeliveryAmount = computed(() => {
    return hasFreeDelivery.value && savedDeliveryEuro.value > 0;
});
const freeDeliveryText = computed(() => {
    return terms.value?.free || 'FREE';
});
const savedDeliveryText = computed(() => {
    const defaultTemplate = 'You saved $AMOUNT$ on delivery!';
    const rawTemplate = terms.value?.saved_delivery_amount || defaultTemplate;
    const euroStr = `${savedDeliveryEuro.value} ${terms.value?.currency_euro || 'EUR'}`;
    const levaStr = `${savedDeliveryLeva.value} ${terms.value?.currency_leva || 'BGN'}`;
    return rawTemplate.replace('$AMOUNT$', `${euroStr} (${levaStr})`);
});
const offersExploreOtherTitle = computed(() => {
    return terms.value?.offers_explore_other_title || 'Explore our other special offers';
});
const hasFinalPrice = computed(() => {
    return !isNaN(orderFinalPriceLeva.value) && orderFinalPriceLeva.value > 0;
});
const deliveryCompanyName = computed(() => {
    return _.find(deliveryCompanies.value, (dt) => dt.value === order.deliveryCompany).text.trim();
});

// Watch specific fields in the order object
watch(
    () => order.deliveryCompany,
    async (newCompany, oldCompany) => {
        foundCities.value = [];
        foundStreets.value = [];
        foundOffices.value = [];
        order.city = null;
        order.street = null;
        order.office = null;
        await debouncedSearchCities();
    }
);
watch(
    () => order.deliveryTo,
    async (newDeliveryTo, oldDeliveryTo) => {
        if (newDeliveryTo == DELIVERY_TO_ADDRESS)
            order.office = null;
        if (newDeliveryTo == DELIVERY_TO_OFFICE)
            order.street = null;
    }
);
watch(
    () => order.city,
    async (newCity, oldCity) => {
        if (newCity != null) {
            await debouncedSearchStreets();
            await debouncedSearchOffices();
            order.street = null;
            order.office = null;
        }
    }
);
const calculateDeliveryCost = async () => {
    let data = { isValid: false };
    try {
        const currentCity = order.city;
        const currentOffice = order.office;
        const currentDeliveryTo = order.deliveryTo;
        const currentPaymentWith = order.paymentWith;
        const currentQuantity = orderQuantity.value;

        const payload = {
            products: products.value,
            city: currentCity,
            office: currentOffice,
            isCOD: currentPaymentWith === PAYMENT_COD
        };
        const hasCity = currentCity !== null && currentCity !== undefined && (typeof currentCity === 'number' ? currentCity > 0 : Boolean(currentCity));
        const hasOffice = currentOffice !== null && currentOffice !== undefined && (typeof currentOffice === 'number' ? currentOffice > 0 : Boolean(currentOffice));
        const isOfficeValid = currentDeliveryTo !== DELIVERY_TO_OFFICE || hasOffice;

        if (hasCity && isOfficeValid && currentQuantity > 0 && products.value && products.value.length > 0) {
            const companyName = _.find(deliveryCompanies.value, (dt) => dt.value === order.deliveryCompany)?.text?.trim()?.toLowerCase() || 'speedy';
            const response = await axios.post(`/${companyName}/delivery/calculate/${store.state.languageCode}`, payload);
            data = response.data;
        }
    } catch (error) {
        console.error("Failed to fetch calculation:", error);
    }
    deliveryCostIsValid.value = Boolean(data?.isValid);
    if (deliveryCostIsValid.value) {
        deliveryCostLeva.value = data.costLeva;
        deliveryCostEuro.value = data.costEuro;
        originalDeliveryCostLeva.value = data.originalCostLeva || data.costLeva;
        originalDeliveryCostEuro.value = data.originalCostEuro || data.costEuro;
        store.commit('setCalculatedDeliveryCost', {
            euro: data.costEuro,
            leva: data.costLeva,
            originalEuro: data.originalCostEuro || data.costEuro,
            originalLeva: data.originalCostLeva || data.costLeva,
            isValid: true
        });
    }
    else {
        deliveryCostLeva.value = null;
        deliveryCostEuro.value = null;
        originalDeliveryCostLeva.value = null;
        originalDeliveryCostEuro.value = null;
        store.commit('setCalculatedDeliveryCost', null);
    }
};

watch(
    [() => products.value, () => order.deliveryTo, () => order.city, () => order.office, () => order.paymentWith, () => order.deliveryCompany],
    async () => {
        await calculateDeliveryCost();
    },
    { deep: true, immediate: true }
);

const searchCities = async (searchTerm) => {
    try {
        formLoading.value = true;
        const companyName = _.find(deliveryCompanies.value, (dt) => dt.value === order.deliveryCompany)?.text?.toLowerCase();
        if (!companyName) {
            return;
        }
        const route = `/${companyName}/city/${store.state.languageCode}`;
        const params = { params: { name: searchTerm } };

        //mobileDebug.log('Search Cities: ', { searchTerm, route, params });
        const response = await axios.get(route, params);
        //mobileDebug.log('Search Cities Results: ', { response: response.data });

        foundCities.value = Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        orderValidationMessages.value = [error];
        console.error("Failed to fetch cities:", error);
    } finally {
        formLoading.value = false;
    }
};
const debouncedSearchCities = _.debounce(async (searchTerm) => {
    await searchCities(searchTerm);
}, debounceTime);
const searchCitiesInput = (event) => {
    const searchTerm = event.target.value?.trim();
    debouncedSearchCities(searchTerm);
};

const searchStreets = async (searchTerm) => {
    try {
        formLoading.value = true;
        const companyName = _.find(deliveryCompanies.value, (dt) => dt.value === order.deliveryCompany)?.text?.toLowerCase();
        if (!companyName) {
            return;
        }
        const response = await axios.get(`/${companyName}/street/${store.state.languageCode}/${order.city}`, { params: { name: searchTerm } });
        foundStreets.value = Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error("Failed to fetch cities:", error);
    } finally {
        formLoading.value = false;
    }
};
const debouncedSearchStreets = _.debounce(async (searchTerm) => {
    await searchStreets(searchTerm);
}, debounceTime);
const searchStreetInput = (event) => {
    const searchTerm = event.target.value;
    debouncedSearchStreets(searchTerm);
};

const searchOffices = async (searchTerm) => {
    try {
        formLoading.value = true;
        const companyName = _.find(deliveryCompanies.value, (dt) => dt.value === order.deliveryCompany)?.text?.toLowerCase();
        if (!companyName) {
            return;
        }
        let response = await axios.get(`/${companyName}/office/${store.state.languageCode}/${order.city}`, { params: { name: searchTerm } });
        let foundOfficesData = Array.isArray(response.data) ? response.data : [];
        foundOfficesData = _.map(foundOfficesData, (d) => {
            return { id: d.id, title: `(${d.id}) ${d.name} ${d.address}` };
        });
        foundOffices.value = foundOfficesData;
    } catch (error) {
        console.error("Failed to fetch cities:", error);
    } finally {
        formLoading.value = false;
    }
};
const debouncedSearchOffices = _.debounce(async (searchTerm) => {
    await searchOffices(searchTerm);
}, debounceTime);
const searchOfficesInput = (event) => {
    const searchTerm = event.target.value;
    debouncedSearchOffices(searchTerm);
};

watch(
    order,
    async () => {
        if (orderValidationMessages.value.length && orderFormRef.value) {
            const validation = await orderFormRef.value.validate();
            if (validation.valid) {
                orderValidationMessages.value = [];
            }
        }
    },
    { deep: true }
);

watch(
    () => order.includeInvoice,
    (include) => {
        if (!include) {
            order.invoiceCompanyName = null;
            order.invoiceBulstat = null;
            order.invoiceMol = null;
            order.invoiceCity = null;
            order.invoiceAddress = null;
            order.invoiceRegisterByDDS = false;
            if (orderFormRef.value) {
                orderFormRef.value.resetValidation();
            }
        }
    }
);

const submitOrderAsync = async () => {
    if (!cartItems.value.length) return;
    const validation = await orderFormRef.value.validate();
    if (!validation.valid) {
        orderValidationMessages.value = [terms.value.validation_errors];
        return;
    }

    orderValidationMessages.value = [];
    showLoadingOrder.value = true;
    try {
        //Calculated fields
        let orderDetails = Object.keys(order).reduce((acc, key) => {
            if (order[key] !== null) {
                acc[key] = order[key];
            }
            return acc;
        }, {});
        orderDetails.streetName = order.street ?
            _.find(foundStreets.value, (c) => c.id === order.street).name :
            null;


        //Validate address
        switch (order.deliveryCompany) {
            case DELIVERY_COMPANY_ECONT:
                const validateEcontPayload = {
                    products: products.value,
                    clientName: `${order.firstName} ${order.lastName}`,
                    clientPhone: order.phone,
                    clientEmail: order.email,
                    officeId: order.office,
                    addressSiteId: order.city,
                    addressStreetName: orderDetails.streetName,
                    addressStreetNumber: order.streetNumber,
                    addressBuilding: order.building,
                    addressEntrance: order.entrance,
                    addressFloor: order.floor,
                    addressApartment: order.apartmentNumber,
                    isCOD: order.paymentWith === PAYMENT_COD,
                    hasInvoice: order.includeInvoice,
                    hasFiscalReceipt: order.includeFiscalReceipt
                };
                await axios.post(`/econt/delivery/validate/${store.state.languageCode}`, validateEcontPayload);
                break;
            case DELIVERY_COMPANY_SPEEDY:
                const validateSpeedyPayload = {
                    products: products.value,
                    clientName: `${order.firstName} ${order.lastName}`,
                    clientPhone: order.phone,
                    clientEmail: order.email,
                    officeId: order.office,
                    addressSiteId: order.city,
                    addressStreetId: order.street,
                    addressStreetNumber: order.streetNumber,
                    addressBuilding: order.building,
                    addressEntrance: order.entrance,
                    addressFloor: order.floor,
                    addressApartment: order.apartmentNumber,
                    isCOD: order.paymentWith === PAYMENT_COD,
                    hasInvoice: order.includeInvoice,
                    hasFiscalReceipt: order.includeFiscalReceipt
                };
                await axios.post(`/speedy/delivery/validate/${store.state.languageCode}`, validateSpeedyPayload);
                break;
        }

        //Prepare order
        orderDetails.products = products.value;
        orderDetails.isCOD = order.paymentWith === PAYMENT_COD;
        orderDetails.deliveryCompanyName = deliveryCompanyName.value;
        orderDetails.deliveryToName = _.find(deliveryTypes.value, (dt) => dt.value === order.deliveryTo).text;
        orderDetails.paymentWithName = _.find(paymentTypes.value, (pt) => pt.value === order.paymentWith).text;
        orderDetails.cityName = order.city ? _.find(foundCities.value, (c) => c.id === order.city).name : null;
        orderDetails.officeName = order.office ? _.find(foundOffices.value, (c) => c.id === order.office).title : null;

        if (!order.includeInvoice) {
            orderDetails.invoiceCompanyName = null;
            orderDetails.invoiceBulstat = null;
            orderDetails.invoiceMol = null;
            orderDetails.invoiceCity = null;
            orderDetails.invoiceAddress = null;
            orderDetails.invoiceRegisterByDDS = false;
        }

        //Send order
        const orderResultResponse = await axios.post(`/order/${store.state.languageCode}`, orderDetails);
        const confirmedOrder = orderResultResponse?.data ?? {};

        if (confirmedOrder.number) {
            const eventId = `order_${confirmedOrder.number}`;
            gtagOrder({
                transaction_id: confirmedOrder.number,
                value: confirmedOrder.amount,
                currency: "EUR"
            }, {
                email: order.email,
                phone: order.phone,
                firstName: order.firstName,
                lastName: order.lastName
            });
            fbPixelOrder({
                transaction_id: confirmedOrder.number,
                value: confirmedOrder.amount,
                currency: "EUR"
            }, eventId);
        }

        if (confirmedOrder.checkoutUrl) {
            window.location.href = confirmedOrder.checkoutUrl;
            return;
        }

        orderFormRef.value.reset();
        store.commit('clearCart');
        sessionStorage.removeItem(SESSION_STORAGE_KEY);

        router.push({ name: 'order-details', query: { order: confirmedOrder.compressed } });
    } catch (error) {
        console.error("Order create failed", error);
        const validations = error.response?.data?.validations ?? [];
        if (validations.length) {
            let context = error.response?.data?.context;
            context = context ? `${context}: ` : "";
            orderValidationMessages.value = _.map(validations, v => `${context}${v}`);
            return;
        }
        orderValidationMessages.value = [terms.value.order_unsuccessful];
    }
    finally {
        showLoadingOrder.value = false;
    }
};

onMounted(async () => {
    window.addEventListener('resize', updateWindowWidth);
    await searchCities();
    await calculateDeliveryCost();
});

onUnmounted(() => {
    window.removeEventListener('resize', updateWindowWidth);
});
</script>

<style scoped>
/*PAGE-ORDER*/

.order-form {
    max-width: 1200px;
    padding: 0px 0px 30px 0px !important;
}

.empty-cart-container {
    animation: emptyCartPop 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

@keyframes emptyCartPop {
    0% {
        opacity: 0;
        transform: scale(0.85) translateY(20px);
    }

    100% {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

.empty-cart-bounce-icon {
    animation: floatIcon 3s ease-in-out infinite;
}

@keyframes floatIcon {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-10px);
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.4s ease, transform 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

.payment-type-card,
.delivery-type-card {
    border: 1px solid rgba(0, 0, 0, 0.15);
    background-color: #f9f9f9;
    transition: all 0.25s ease-in-out;
    margin-right: 8px;
}

.payment-type-card--selected,
.delivery-type-card--selected {
    border-color: #ffc451;
    background-color: rgba(255, 196, 81, 0.15);
    box-shadow: 0 3px 10px rgba(255, 196, 81, 0.25);
}

.payment-type-text,
.delivery-type-text {
    font-size: 0.95rem;
}

.remove-btn {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 5;
}

.acknowledgement {
    padding-top: 40px;
}

.offers-banner-clickable {
    cursor: pointer;
    transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.offers-banner-clickable:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18) !important;
}

section {
    padding: 20px 0px;
}

section:first-of-type {
    padding-top: 0px !important;
}

.section-title {
    padding-bottom: 10px;
}

.quantity-input .v-field__input {
    text-align: center !important;
}

.quantity-btn {
    flex: 1;
    margin-left: auto;
    margin-right: auto;
    width: 40%;
    margin-bottom: 4px;
}

.product-image {
    height: 200px;
    /* Reduced base height */
}

@media (max-width: 600px) {
    .product-image {
        height: 120px;
        /* Much smaller on mobile for 2rd layout */
    }

    .v-card-title {
        padding: 8px !important;
        font-size: 0.8rem !important;
    }

    .v-card-text {
        padding: 4px !important;
    }

    .quantity-btn {
        min-width: 36px !important;
        height: 32px !important;
    }
}

.quantity-adjustment {
    display: flex;
    justify-content: space-evenly;
    align-content: center;
    align-items: center;
}

.pay-attention-note {
    font-style: italic;
    font-size: small;
    color: #242424;
}


.error-message {
    margin-top: 20px;
    color: #eb3d3d;
    background: #efefef;
    text-align: left;
    padding: 15px;
    font-weight: 600;
}

.note-color {
    color: #242424;
}


::v-deep(label.v-label.v-label--clickable) {
    padding-left: 6px !important;
    padding-right: 6px !important;
}

::v-deep(.delivery-company-radio-group label.v-label) {
    opacity: 1 !important;
    padding-left: 0px !important;
    padding-right: 0px !important;
}


.order-to-save-btn {
    background: linear-gradient(45deg, #000000, #FFC451);
    color: #f3f3d9;
    font-size: 0.875rem;
    font-weight: bold;
    padding: 5px 10px;
    margin: 4px;
    border: none;
    border-radius: 20px;
    cursor: pointer;
    transition: 0.3s ease-in-out;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4);
    position: relative;
    overflow: hidden;
}

.order-to-save-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 15px rgba(255, 196, 81, 0.5);
}

.order-to-save-btn::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.2);
    transition: 0.4s;
}

.order-to-save-btn:hover::after {
    left: 100%;
}

.order-to-save-btn span b {
    font-size: 22px;
    font-weight: bold;
}

/* Allows wrapping; avoid ellipsis */
.wrap-title {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    word-break: break-word;
    hyphens: auto;
}

/* Fluid typography with controlled bounds */
.responsive-title {
    /* Tweak these once; reused everywhere */
    --fs-min: 0.7rem;
    /* ~16px on small phones */
    --fs-ideal: 1.2vw;
    /* fluid growth between min/max */
    --fs-max: 1rem;
    /* ~28px on large desktops */

    font-size: clamp(var(--fs-min), var(--fs-ideal), var(--fs-max));
    line-height: 1.25;
    /* why: keep dense titles readable */
    font-weight: 600;
}


@property --cart-border-angle {
    syntax: "<angle>";
    inherits: false;
    initial-value: 0deg;
}

@keyframes spinCartBorder {
    to {
        --cart-border-angle: 360deg;
    }
}

.product-card {
    position: relative;
    border: 2px solid rgba(255, 196, 81, 0.7) !important;
    border-radius: 16px !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06), 0 0 10px rgba(255, 196, 81, 0.15) !important;
    transition: transform 0.35s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.35s cubic-bezier(0.25, 1, 0.5, 1), border-color 0.35s ease !important;
    display: flex;
    flex-direction: column;
    background: #ffffff !important;
}

.product-card::before {
    content: "";
    position: absolute;
    inset: -3px;
    border-radius: 18px;
    padding: 3px;
    background: conic-gradient(from var(--cart-border-angle),
            transparent 0deg,
            rgba(255, 196, 81, 0.4) 60deg,
            #ffc451 120deg,
            #ffffff 140deg,
            #ffc451 160deg,
            rgba(255, 196, 81, 0.4) 220deg,
            transparent 280deg);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
    z-index: 10;
    animation: spinCartBorder 3.5s linear infinite;
}

.product-card:hover::before {
    opacity: 1;
}

.product-card:hover {
    border-color: #ffc451 !important;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12), 0 0 25px rgba(255, 196, 81, 0.5) !important;
    transform: translateY(-4px) !important;
}

.product-name {
    font-size: clamp(1rem, 1.15vw, 1.25rem);
    font-weight: 700;
    color: #ffc451;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
    -webkit-text-stroke: 0.5px #cc9900;
    letter-spacing: 0.5px;
    min-height: 2.8em;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1.3;
    text-align: center;
}

.product-price-row {
    color: #666;
    font-size: 0.9rem;
}

.quantity-controls {
    display: flex;
    gap: 8px;
    justify-content: center;
    padding-bottom: 8px;
}

@media (max-width: 600px) {
    .quantity-controls {
        gap: 4px;
        padding: 0 4px 8px 4px;
    }

    .order-form {
        padding-left: 6px !important;
        padding-right: 6px !important;
    }

    .section-title {
        margin-top: 10px !important;
    }

    .section-title h4 {
        font-size: 0.95rem !important;
    }

    ::v-deep(.v-field) {
        font-size: 0.88rem;
    }

    .delivery-type-card,
    .payment-type-card {
        padding: 4px 8px !important;
        font-size: 0.78rem !important;
    }

    .delivery-type-card .v-icon,
    .payment-type-card .v-icon {
        font-size: 18px !important;
    }

    ::v-deep(.delivery-type-radio-group .v-selection-control-group),
    ::v-deep(.payment-radio-group .v-selection-control-group) {
        flex-direction: column !important;
        align-items: flex-start !important;
        gap: 6px !important;
    }

    ::v-deep(.delivery-company-radio-group .v-selection-control-group) {
        flex-direction: row !important;
        justify-content: flex-start !important;
        gap: 8px !important;
    }

    ::v-deep(.v-selection-control) {
        min-height: 36px !important;
    }

    ::v-deep(.acknowledgement .v-checkbox) {
        margin-bottom: -12px !important;
    }

    ::v-deep(.acknowledgement .v-label) {
        font-size: 0.78rem !important;
        line-height: 1.3 !important;
    }

    .product-card {
        padding: 4px !important;
    }

    .product-name {
        font-size: 0.78rem !important;
        line-height: 1.25 !important;
        font-weight: 700 !important;
    }

    .product-price-row {
        font-size: 0.72rem !important;
    }

    .product-image {
        height: 100px !important;
    }

    .quantity-stepper .step-btn {
        width: 22px !important;
        height: 28px !important;
        font-size: 14px !important;
    }

    .quantity-stepper .qty-input {
        width: 30px !important;
        height: 28px !important;
        font-size: 13px !important;
    }

    .btn-remove-cart {
        min-width: 28px !important;
        width: 28px !important;
        height: 28px !important;
        min-height: 28px !important;
        padding: 0 !important;
    }
}

.offers-outer-title {
    font-size: 20px;
    font-weight: 600;
    font-family: "Poppins", "Raleway", sans-serif;
    color: #1a1a1a;
    margin-bottom: 20px;
    max-width: 850px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.4;
}

@media (max-width: 920px) {
    .offers-outer-title {
        font-size: 16px !important;
        margin-bottom: 14px !important;
    }
}

@media (max-width: 480px) {
    .offers-outer-title {
        font-size: 14px !important;
        margin-bottom: 12px !important;
    }
}



/* Offers CTA Section Signature Border & Interactive Product Tiles Grid */
.signature-offers-card {
    position: relative;
    background: transparent;
    padding: 0;
    border: none !important;
    box-shadow: none !important;
}

.swipeable-container {
    touch-action: pan-y;
    user-select: none;
    -webkit-user-select: none;
    width: 100%;
    overflow: hidden;
}

.swipe-next-enter-active,
.swipe-next-leave-active,
.swipe-prev-enter-active,
.swipe-prev-leave-active {
    transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.25, 1, 0.5, 1);
}

.swipe-next-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.swipe-next-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

.swipe-prev-enter-from {
    opacity: 0;
    transform: translateX(-30px);
}

.swipe-prev-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

@keyframes bounceEndLeft {
    0% {
        transform: translateX(0);
        box-shadow: inset 0px 0 0px transparent;
    }

    30% {
        transform: translateX(-22px);
        box-shadow: inset -8px 0 16px -3px rgba(255, 196, 81, 0.65);
    }

    60% {
        transform: translateX(5px);
        box-shadow: inset -4px 0 8px -3px rgba(255, 196, 81, 0.35);
    }

    80% {
        transform: translateX(-2px);
        box-shadow: inset -1px 0 4px -2px rgba(255, 196, 81, 0.15);
    }

    100% {
        transform: translateX(0);
        box-shadow: inset 0px 0 0px transparent;
    }
}

@keyframes bounceEndRight {
    0% {
        transform: translateX(0);
        box-shadow: inset 0px 0 0px transparent;
    }

    30% {
        transform: translateX(22px);
        box-shadow: inset 8px 0 16px -3px rgba(255, 196, 81, 0.65);
    }

    60% {
        transform: translateX(-5px);
        box-shadow: inset 4px 0 8px -3px rgba(255, 196, 81, 0.35);
    }

    80% {
        transform: translateX(2px);
        box-shadow: inset 1px 0 4px -2px rgba(255, 196, 81, 0.15);
    }

    100% {
        transform: translateX(0);
        box-shadow: inset 0px 0 0px transparent;
    }
}

.bounce-end-left {
    animation: bounceEndLeft 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.bounce-end-right {
    animation: bounceEndRight 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.product-tiles-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-content: flex-start;
    gap: 12px;
    width: 100%;
    min-height: 332px;
}

.offers-placeholder-tile {
    visibility: hidden !important;
    pointer-events: none !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
}

.interactive-product-tile {
    position: relative;
    flex: 1 1 calc(25% - 12px);
    min-width: 200px;
    max-width: 280px;
    height: 160px;
    border-radius: 16px !important;
    overflow: hidden !important;
    isolation: isolate;
    -webkit-mask-image: -webkit-radial-gradient(white, black);
    mask-image: radial-gradient(white, black);
    background: #ffffff;
    border: 1.5px solid rgba(255, 196, 81, 0.4);
    cursor: pointer;
    transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

@media (max-width: 860px) {
    .product-tiles-grid {
        min-height: 302px;
    }

    .interactive-product-tile {
        flex: 1 1 calc(33.333% - 10px);
        min-width: 160px;
        height: 145px;
    }
}

@media (max-width: 540px) {
    .product-tiles-grid {
        min-height: 272px;
    }

    .interactive-product-tile {
        flex: 1 1 calc(50% - 8px);
        min-width: 130px;
        height: 130px;
        border-radius: 12px !important;
    }
}

.tile-image-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-size: cover;
    background-position: center;
    transition: transform 0.4s ease, filter 0.4s ease;
}

@property --tile-border-angle {
    syntax: "<angle>";
    inherits: false;
    initial-value: 0deg;
}

@keyframes spinTileBorder {
    to {
        --tile-border-angle: 360deg;
    }
}

.interactive-product-tile::before {
    content: "";
    position: absolute;
    inset: -3px;
    border-radius: 18px;
    padding: 3px;
    background: conic-gradient(from var(--tile-border-angle, 0deg),
            transparent 0deg,
            rgba(255, 196, 81, 0.4) 60deg,
            #ffc451 120deg,
            #ffffff 140deg,
            #ffc451 160deg,
            rgba(255, 196, 81, 0.4) 220deg,
            transparent 280deg);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
    z-index: 10;
    animation: spinTileBorder 3.5s linear infinite;
}

.interactive-product-tile:hover::before {
    opacity: 1;
}

.interactive-product-tile:hover {
    transform: translateY(-6px) scale(1.03);
    border-color: #ffc451 !important;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12), 0 0 25px rgba(255, 196, 81, 0.5) !important;
    z-index: 5;
}

.interactive-product-tile:hover .tile-image-bg {
    transform: scale(1.12);
    filter: brightness(1.12);
}

.tile-hover-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle, rgba(255, 196, 81, 0.25) 0%, rgba(0, 0, 0, 0.6) 100%);
    opacity: 0;
    transition: opacity 0.35s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
}

.interactive-product-tile:hover .tile-hover-overlay {
    opacity: 1;
}

.tile-hover-icon {
    background: #ffc451;
    color: #111;
    border-radius: 50%;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
    transform: scale(0.7);
    transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.interactive-product-tile:hover .tile-hover-icon {
    transform: scale(1);
}

.tile-badge {
    position: relative;
    z-index: 3;
    background: rgba(20, 20, 20, 0.94);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    padding: 8px 8px;
    border-top: 1.5px solid rgba(255, 196, 81, 0.45);
    border-bottom-left-radius: 14px !important;
    border-bottom-right-radius: 14px !important;
    text-align: center;
    width: 100%;
    min-height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
}

@media (max-width: 540px) {
    .tile-badge {
        border-bottom-left-radius: 10px !important;
        border-bottom-right-radius: 10px !important;
        padding: 6px 4px;
        min-height: 38px;
    }
}

.tile-title {
    font-size: 0.82rem;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0.2px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.25;
    text-align: center;
    transition: color 0.2s ease;
}

.interactive-product-tile:hover .tile-title {
    color: #ffc451;
}

@media (max-width: 540px) {
    .tile-title {
        font-size: 0.74rem;
        line-height: 1.2;
    }
}

.offers-gold-btn {
    font-size: 1rem !important;
    padding: 12px 36px !important;
    margin: 0 !important;
    background: linear-gradient(135deg, #1f1a14 0%, #ffc451 100%) !important;
    color: #ffffff !important;
    border-radius: 30px !important;
    font-weight: 700 !important;
    border: none !important;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3) !important;
    transition: transform 0.25s ease, box-shadow 0.25s ease !important;
}

.offers-gold-btn:hover {
    transform: translateY(-2px) scale(1.03) !important;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4) !important;
}

@media (max-width: 600px) {
    .offers-gold-btn {
        font-size: 0.92rem !important;
        padding: 10px 22px !important;
        max-width: 90% !important;
    }
}
</style>